/**
 * BrandedImage Component
 * Ensures all image alt text includes "Fridge.com"
 * Use this instead of Next Image to guarantee branding
 */

import { type ImageProps } from 'next/image'
import type { ReactNode } from 'react'
import { SafeImage } from '@/components/ui/safe-image'
import { ensureAltBranding } from '@/lib/branding-enforcement'

interface BrandedImageProps extends Omit<ImageProps, 'alt'> {
  alt?: string
  productName?: string
  /** FRIDGE-635 — forwarded to SafeImage's photo → brand logo → mark chain. */
  brandLogoSrc?: string | null
  /** Family-specific treatment for missing, rejected or failed photos. */
  fallbackContent?: ReactNode
}

/**
 * Image component that adds Fridge.com branding to alt text
 * @param src - Image source URL
 * @param alt - Optional alt text (branding is added by this component)
 * @param productName - Optional product/item name for context
 * @param props - All other Next/Image props
 */
export function BrandedImage({ 
  src, 
  alt, 
  productName,
  ...props 
}: BrandedImageProps) {
  const brandedAlt = ensureAltBranding(alt, productName)
  
  return (
    <SafeImage 
      src={src}
      alt={brandedAlt}
      {...props}
    />
  )
}

/**
 * Convenience exports for common use cases
 */

// Product image with alt text from the product name
export function ProductImage({ 
  product, 
  imageIndex = 0,
  ...props 
}: Omit<BrandedImageProps, 'src' | 'productName'> & { 
  product: { images?: string[], title?: string, name?: string },
  imageIndex?: number 
}) {
  const src = product.images?.[imageIndex] || '/placeholder.svg'
  const name = product.title || product.name || 'Product'
  
  return (
    <BrandedImage
      src={src}
      productName={name}
      {...props}
    />
  )
}

// Collection image with branded alt text
export function CollectionImage({ 
  collection,
  ...props 
}: Omit<BrandedImageProps, 'src' | 'productName'> & { 
  collection: { image?: string, title?: string, name?: string }
}) {
  const src = collection.image || '/placeholder.svg'
  const name = collection.title || collection.name || 'Collection'
  
  return (
    <BrandedImage
      src={src}
      alt={`${name} collection`}
      {...props}
    />
  )
}

// Blog article image with branded alt text
export function ArticleImage({ 
  article,
  ...props 
}: Omit<BrandedImageProps, 'src'> & { 
  article: { image?: string, title?: string }
}) {
  const src = article.image || '/placeholder.svg'
  
  return (
    <BrandedImage
      src={src}
      alt={props.alt || article.title}
      {...props}
    />
  )
}
